"use client";

import React, { createContext, useContext, useReducer, useEffect } from "react";
import { Cart } from "@nusrif/types";
import {
  getCartFromServer,
  addItemToCart,
  adjustCartItem,
  removeCartItem,
  canLoadCart,
} from "@nusrif/services";
import { trackEvent } from "@nusrif/services";

interface CartState {
  cart: Cart | null;
  isLoading: boolean;
  error: string | null;
  isSyncing: boolean;
}

type CartAction =
  | { type: "SET_CART"; payload: Cart }
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "SET_ERROR"; payload: string | null }
  | { type: "SET_SYNCING"; payload: boolean }
  | { type: "CLEAR_CART" };

interface CartContextType {
  state: CartState;
  addItem: (
    productVariantId: string,
    quantity: number
  ) => Promise<{ success: boolean; error?: string }>;
  updateQuantity: (
    itemId: string,
    quantity: number
  ) => Promise<{ success: boolean; error?: string }>;
  removeItem: (itemId: string) => Promise<void>;
  clearCart: () => Promise<void>;
  refreshCart: () => Promise<void>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const cartReducer = (state: CartState, action: CartAction): CartState => {
  switch (action.type) {
    case "SET_CART":
      return {
        ...state,
        cart: action.payload,
        error: null,
      };

    case "SET_LOADING":
      return {
        ...state,
        isLoading: action.payload,
      };

    case "SET_ERROR":
      return {
        ...state,
        error: action.payload,
      };

    case "SET_SYNCING":
      return {
        ...state,
        isSyncing: action.payload,
      };

    case "CLEAR_CART":
      return {
        ...state,
        cart: null,
        error: null,
      };

    default:
      return state;
  }
};

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(cartReducer, {
    cart: null,
    isLoading: false,
    error: null,
    isSyncing: false,
  });

  // Load cart from server on mount
  useEffect(() => {
    const loadInitialCart = async () => {
      console.log("[CartContext] Loading initial cart from server");
      dispatch({ type: "SET_LOADING", payload: true });
      try {
        const response = await getCartFromServer();
        if (response.success && response.data) {
          dispatch({ type: "SET_CART", payload: response.data });
        }
      } catch (error) {
        console.error("Error loading cart:", error);
        dispatch({ type: "SET_ERROR", payload: "Failed to load cart" });
      } finally {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    };

    if (canLoadCart()) {
      loadInitialCart();
    }
  }, []);

  const refreshCart = async () => {
    console.log("[CartContext] refreshCart called");
    dispatch({ type: "SET_SYNCING", payload: true });
    try {
      if (!canLoadCart()) {
        console.log(
          "[CartContext] Cannot refresh cart, user not authenticated"
        );
        dispatch({
          type: "SET_ERROR",
          payload: "Cannot refresh cart, user not authenticated",
        });
        return;
      }

      const response = await getCartFromServer();
      console.log("[CartContext] refreshCart response:", response);
      if (response.success && response.data) {
        dispatch({ type: "SET_CART", payload: response.data });
      } else {
        dispatch({
          type: "SET_ERROR",
          payload: response.message || "Failed to refresh cart",
        });
      }
    } catch (error) {
      console.error("[CartContext] Error refreshing cart:", error);
      dispatch({ type: "SET_ERROR", payload: "Failed to refresh cart" });
    } finally {
      dispatch({ type: "SET_SYNCING", payload: false });
    }
  };

  const addItem = async (
    productVariantId: string,
    quantity: number
  ): Promise<{ success: boolean; error?: string }> => {
    console.log("[CartContext] addItem called", { productVariantId, quantity });
    dispatch({ type: "SET_SYNCING", payload: true });
    try {
      const response = await addItemToCart({ productVariantId, quantity });
      console.log("[CartContext] addItem response:", response);
      if (response.success && response.data) {
        dispatch({ type: "SET_CART", payload: response.data });

        // Track successful add to cart
        const addedItem = response.data.lines.find(
          (line) => line.productVariant.id === productVariantId
        );
        if (addedItem) {
          trackEvent({
            event: "add_to_cart",
            properties: {
              product_id: addedItem.productVariant.product.id,
              product_name: addedItem.productVariant.product.name,
              variant_id: productVariantId,
              variant_name: addedItem.productVariant.name,
              quantity: quantity,
              price: addedItem.unitPriceWithTax,
              currency: response.data.currencyCode,
              cart_total: response.data.totalWithTax,
              cart_item_count: response.data.lines.reduce(
                (sum, line) => sum + line.quantity,
                0
              ),
            },
          });
        }

        return { success: true };
      } else {
        const errorMessage = response.message || "Failed to add item to cart";
        dispatch({ type: "SET_ERROR", payload: errorMessage });
        return { success: false, error: errorMessage };
      }
    } catch (error) {
      console.error("[CartContext] Error adding item to cart:", error);
      const errorMessage = "Failed to add item to cart";
      dispatch({ type: "SET_ERROR", payload: errorMessage });
      return { success: false, error: errorMessage };
    } finally {
      dispatch({ type: "SET_SYNCING", payload: false });
    }
  };

  const updateQuantity = async (
    itemId: string,
    quantity: number
  ): Promise<{ success: boolean; error?: string }> => {
    console.log("[CartContext] updateQuantity called", { itemId, quantity });
    dispatch({ type: "SET_SYNCING", payload: true });
    try {
      const response = await adjustCartItem({ orderLineId: itemId, quantity });
      console.log("[CartContext] updateQuantity response:", response);
      if (response.success && response.data) {
        dispatch({ type: "SET_CART", payload: response.data });

        // Track quantity update
        const updatedItem = response.data.lines.find(
          (line) => line.id === itemId
        );
        if (updatedItem) {
          trackEvent({
            event: "update_cart_quantity",
            properties: {
              product_id: updatedItem.productVariant.product.id,
              product_name: updatedItem.productVariant.product.name,
              variant_id: updatedItem.productVariant.id,
              variant_name: updatedItem.productVariant.name,
              new_quantity: quantity,
              price: updatedItem.unitPriceWithTax,
              currency: response.data.currencyCode,
              cart_total: response.data.totalWithTax,
              cart_item_count: response.data.lines.reduce(
                (sum, line) => sum + line.quantity,
                0
              ),
            },
          });
        }
        return { success: true };
      } else {
        dispatch({
          type: "SET_ERROR",
          payload: response.message || "Failed to update quantity",
        });
        return {
          success: false,
          error: response.message || "Failed to update quantity",
        };
      }
    } catch (error: any) {
      console.error("[CartContext] Error updating quantity:", error);
      dispatch({ type: "SET_ERROR", payload: "Failed to update quantity" });
      return {
        success: false,
        error: error?.message || "Failed to update quantity",
      };
    } finally {
      dispatch({ type: "SET_SYNCING", payload: false });
    }
  };

  const removeItem = async (itemId: string) => {
    console.log("[CartContext] removeItem called", { itemId });
    dispatch({ type: "SET_SYNCING", payload: true });
    try {
      // Get item details before removal for tracking
      const itemToRemove = state.cart?.lines.find((line) => line.id === itemId);

      const response = await removeCartItem(itemId);
      console.log("[CartContext] removeItem response:", response);
      if (response.success && response.data) {
        dispatch({ type: "SET_CART", payload: response.data });

        // Track item removal
        if (itemToRemove) {
          trackEvent({
            event: "remove_from_cart",
            properties: {
              product_id: itemToRemove.productVariant.product.id,
              product_name: itemToRemove.productVariant.product.name,
              variant_id: itemToRemove.productVariant.id,
              variant_name: itemToRemove.productVariant.name,
              removed_quantity: itemToRemove.quantity,
              price: itemToRemove.unitPriceWithTax,
              currency: response.data.currencyCode,
              cart_total: response.data.totalWithTax,
              cart_item_count: response.data.lines.reduce(
                (sum, line) => sum + line.quantity,
                0
              ),
            },
          });
        }
      } else {
        dispatch({
          type: "SET_ERROR",
          payload: response.message || "Failed to remove item",
        });
      }
    } catch (error) {
      console.error("[CartContext] Error removing item:", error);
      dispatch({ type: "SET_ERROR", payload: "Failed to remove item" });
    } finally {
      dispatch({ type: "SET_SYNCING", payload: false });
    }
  };

  const clearCart = async () => {
    console.log("[CartContext] clearCart called");
    dispatch({ type: "SET_SYNCING", payload: true });
    try {
      // Track cart clear
      if (state.cart && state.cart.lines.length > 0) {
        trackEvent({
          event: "clear_cart",
          properties: {
            items_cleared: state.cart.lines.length,
            total_items_cleared: state.cart.lines.reduce(
              (sum, line) => sum + line.quantity,
              0
            ),
            cart_total_before_clear: state.cart.totalWithTax,
            currency: state.cart.currencyCode,
          },
        });
      }

      // Remove cart from local storage and clear state only
      localStorage.removeItem("shopping-cart");
      dispatch({ type: "CLEAR_CART" });
      console.log("[CartContext] cart cleared");
    } catch (error) {
      console.error("[CartContext] Error clearing cart locally:", error);
      dispatch({ type: "SET_ERROR", payload: "Failed to clear cart locally" });
    } finally {
      dispatch({ type: "SET_SYNCING", payload: false });
    }
  };

  return (
    <CartContext.Provider
      value={{
        state,
        addItem,
        updateQuantity,
        removeItem,
        clearCart,
        refreshCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};
